import { useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import { Drawer, DialogActions, Button, TextField, Stack, Switch, Typography, Alert } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'

const EditUserDialog = ({ ownUser, editingUser, save, open, setOpen }) => {

  const [username, setUsername] = useState("")
  const [email, setEmail] = useState("")
  const [enabled, setEnabled] = useState(false)
  const [studio, setStudio] = useState(false)
  const [scanner, setScanner] = useState(false)
  const [admin, setAdmin] = useState(false)
  const [errors, setErrors] = useState({ username: "", email: "", permissions: "" })

  const mode = editingUser?.username ? "edit" : "add"

  const resetForm = useMemo(() => ({
    username: "",
    email: "",
    enabled: true,
    studio: false,
    scanner: false,
    admin: false,
  }), [])

  useEffect(() => {
    if (open) {
      if (mode === "edit") {
        setUsername(editingUser.username)
        setEmail(editingUser.email)
        setEnabled(editingUser?.user_enabled === 1)
        setStudio(editingUser?.studio_enabled === 1)
        setScanner(editingUser?.scanner_enabled === 1)
        setAdmin(editingUser?.client_admin_enabled === 1)
      } else {
        setUsername(resetForm.username)
        setEmail(resetForm.email)
        setEnabled(resetForm.enabled)
        setStudio(resetForm.studio)
        setScanner(resetForm.scanner)
        setAdmin(resetForm.admin)
      }
      setErrors({ username: "", email: "", permissions: "" })
    }
  }, [open, mode, editingUser, resetForm])

  useEffect(() => {
    if (admin) setStudio(true)
  }, [admin])

  const close = () => setOpen(false)

  const validate = () => {
    const nextErrors = { username: "", email: "", permissions: "" }

    if (!username.trim()) nextErrors.username = "Username is required."
    if (!email.trim()) nextErrors.email = "Email is required."
    if (!studio && !scanner) nextErrors.permissions = "Enable Studio, Scanner, or both."

    setErrors(nextErrors)

    return !nextErrors.username && !nextErrors.email && !nextErrors.permissions
  }

  const saveUser = () => {
    if (!validate()) return

    let user = _.cloneDeep(editingUser)

    user.username = username.trim()
    user.email = email.trim()
    user.user_enabled = enabled ? 1 : 0
    user.studio_enabled = studio ? 1 : 0
    user.scanner_enabled = scanner ? 1 : 0
    user.client_admin_enabled = admin ? 1 : 0

    save(mode, user)
    close()
  }

  if (!open) return null

  return (

    <Drawer
      open={open}
      anchor="right"
      onClose={close}
      sx={{
        width: 500,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: { width: 500, boxSizing: 'border-box', padding: 5, paddingTop: 10 },
      }}
    >

      <div className="flex flex-col gap-5">
        <div className="flex flex-row justify-between">
          <Typography color="secondary" variant="h6">{editingUser?.username ? "Edit User: " + editingUser.username : "Add User"}
          </Typography>
          <Button endIcon={<EastRoundedIcon />} onClick={close} >cancel</Button>
        </div>

        <div className='p-5 flex flex-col gap-5'>

          <TextField
            value={username}
            onChange={e => setUsername(e.target.value)}
            label="username"
            slotProps={{ inputLabel: { shrink: true } }}
            required
            error={Boolean(errors.username)}
            helperText={errors.username}
          />
          <TextField
            value={email}
            onChange={e => setEmail(e.target.value)}
            label="email"
            slotProps={{ inputLabel: { shrink: true } }}
            required
            error={Boolean(errors.email)}
            helperText={errors.email}
          />

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography>Enabled : no</Typography>
            <Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} disabled={ownUser.user_id === editingUser.user_id} />
            <Typography>yes</Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography>Administrator : no</Typography>
            <Switch checked={admin} onChange={e => setAdmin(e.target.checked)} disabled={ownUser.user_id === editingUser.user_id} />
            <Typography>yes</Typography>
          </Stack>

          <hr />

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography>Allow access to Studio : no</Typography>
            <Switch checked={studio} onChange={e => setStudio(e.target.checked)} disabled={admin} />
            <Typography>yes</Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography>Allow access to Scan App : no</Typography>
            <Switch checked={scanner} onChange={e => setScanner(e.target.checked)} />
            <Typography>yes</Typography>
          </Stack>

          {errors.permissions ? <Alert severity="error">{errors.permissions}</Alert> : null}
        </div>
      </div>

      <DialogActions>
        <div className="flex flex-row justify-end w-full pr-5 pb-5">
          <Button onClick={saveUser} variant='contained'>Save</Button>
        </div>
      </DialogActions>
    </Drawer>

  )
}

export default EditUserDialog
